import {
  Workout,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutSection,
  WorkoutSuperset,
  WorkoutSupersetExercise,
  EntryFullMutationData,
} from '@strength/domain-workout';
import { Sortable, nextSortOrder, targetSortOrderByFn } from '@strength/utils';
import { WorkoutLayerMutationFns } from './mutation-fns/types';
import { Active, DragEndEvent, useDraggable, useDroppable } from '@dnd-kit/core';
import { useState } from 'react';

export const DROPPABLE_CLASSNAME = 'bg-blue-300';

export type WorkoutSectionDraggable = {
  type: 'WorkoutSection';
  workoutSectionId: string;
  workoutId: string;
};

export type WorkoutSectionDroppable =
  | {
      type: 'AddWorkoutSection';
      workoutId: string;
      workoutSectionId?: undefined;
    }
  | WorkoutSectionDraggable;

export type WorkoutExerciseDraggable = {
  type: 'WorkoutExercise';
  workoutExerciseId: string;
  workoutSupersetId: string | null;
  workoutSectionId: string;
  workoutAlternateGroupId: string | null;
  workoutId: string;
};

export type WorkoutExerciseDroppable =
  | {
      type: 'AddWorkoutExercise';
      workoutSectionId: string;
      workoutId: string;
      workoutExerciseId?: undefined;
      workoutSupersetId?: undefined;
    }
  | WorkoutExerciseDraggable;

export type WorkoutExerciseMetricDraggable = {
  type: 'WorkoutExerciseMetric';
  workoutExerciseMetricId: string;
  workoutExerciseId: string;
  workoutSectionId: string;
  workoutId: string;
};

export type WorkoutExerciseMetricDroppable = WorkoutExerciseMetricDraggable;

type AnyDraggable =
  | WorkoutSectionDraggable
  | WorkoutExerciseDraggable
  | WorkoutExerciseMetricDraggable;

// Helper function to determine drop target information
function getDropTarget(workouts: Workout[], drop: WorkoutExerciseDroppable) {
  if (drop.type === 'AddWorkoutExercise') {
    const dropFindResult = findSection(
      workouts,
      drop.workoutId,
      drop.workoutSectionId,
    );
    if (!dropFindResult) return null;

    return {
      targetSectionId: drop.workoutSectionId,
      targetSortOrder: nextSortOrder(dropFindResult.section.entries),
      targetExerciseId: undefined,
      targetSupersetId: undefined,
    };
  } else {
    const dropFindResult = findEntry(
      workouts,
      drop.workoutId,
      drop.workoutSectionId,
      drop.workoutExerciseId,
      drop.workoutSupersetId,
      null, // drop targets don't have alternate group IDs
    );
    if (!dropFindResult) return null;

    const targetSortOrder = dropFindResult.entry.sortOrder;
    let targetExerciseId: string | undefined;
    let targetSupersetId: string | undefined;

    if (dropFindResult.entry.__type === 'WorkoutSuperset') {
      targetSupersetId = dropFindResult.entry.workoutSupersetId;
      targetExerciseId = undefined;
    } else if (drop.workoutSupersetId !== null) {
      targetSupersetId = drop.workoutSupersetId;
      targetExerciseId = undefined;
    } else {
      targetExerciseId = drop.workoutExerciseId;
      targetSupersetId = undefined;
    }

    return {
      targetSectionId: drop.workoutSectionId,
      targetSortOrder,
      targetExerciseId,
      targetSupersetId,
    };
  }
}

function handleCrossSectionExerciseMove(
  workouts: Workout[],
  drag: WorkoutExerciseDraggable,
  drop: WorkoutExerciseDroppable,
  mutationFns: WorkoutLayerMutationFns,
) {
  const dragFindResult = findEntry(
    workouts,
    drag.workoutId,
    drag.workoutSectionId,
    drag.workoutExerciseId,
    drag.workoutSupersetId,
    drag.workoutAlternateGroupId,
  );
  if (!dragFindResult) return;

  const dropTarget = getDropTarget(workouts, drop);
  if (!dropTarget) return;

  const entries: EntryFullMutationData[] = [dragFindResult.entry];

  if (dragFindResult.entry.__type === 'WorkoutSuperset') {
    const superset = dragFindResult.entry;
    const supersetExerciseIds = new Set(
      superset.exercises.map((ex) => ex.exercise.workoutExerciseId),
    );

    const alternateGroups = dragFindResult.section.entries.filter(
      (entry): entry is WorkoutAlternateGroup => {
        if (entry.__type !== 'WorkoutAlternateGroup') return false;

        // Check if any exercise in this alternate group is in the superset
        return entry.exercises.some(({ exercise }) =>
          supersetExerciseIds.has(exercise.workoutExerciseId),
        );
      },
    );

    entries.push(...alternateGroups);
  }
  // Will only send over more than one entry if item dragged is in both a superset and an alternate group
  mutationFns.onMoveEntriesIntoNewSection(
    drag.workoutId,
    entries,
    dropTarget.targetSectionId,
    dropTarget.targetSortOrder,
  );
}

export function handleDragEnd(
  { active, over }: DragEndEvent,
  workouts: Workout[],
  mutationFns: WorkoutLayerMutationFns,
) {
  if (!active || !over) {
    return;
  }

  if (active.data.current?.['type'] === 'WorkoutExercise') {
    const drag = active.data.current as WorkoutExerciseDraggable;
    if (
      over.data.current?.['type'] === 'WorkoutExercise' ||
      over.data.current?.['type'] === 'AddWorkoutExercise'
    ) {
      const drop = over.data.current as WorkoutExerciseDroppable;
      handleExerciseDnD(workouts, drag, drop, mutationFns);
    }
  }

  if (active.data.current?.['type'] === 'WorkoutSection') {
    const drag = active.data.current as WorkoutSectionDraggable;
    if (
      over.data.current?.['type'] === 'WorkoutSection' ||
      over.data.current?.['type'] === 'AddWorkoutSection'
    ) {
      const drop = over.data.current as WorkoutSectionDroppable;
      handleSectionDnD(workouts, drag, drop, mutationFns);
    }
  }

  if (active.data.current?.['type'] === 'WorkoutExerciseMetric') {
    const drag = active.data.current as WorkoutExerciseMetricDraggable;
    if (over.data.current?.['type'] === 'WorkoutExerciseMetric') {
      const drop = over.data.current as WorkoutExerciseMetricDroppable;
      handleExerciseMetricDnD(workouts, drag, drop, mutationFns);
    }
  }
}

function handleExerciseDnD(
  workouts: Workout[],
  drag: WorkoutExerciseDraggable,
  drop: WorkoutExerciseDroppable,
  mutationFns: WorkoutLayerMutationFns,
) {
  if (
    drop.type === 'WorkoutExercise' &&
    drag.workoutExerciseId === drop.workoutExerciseId
  ) {
    return;
  }

  // Prevent dragging exercises between different workouts
  if (drag.workoutId !== drop.workoutId) {
    return;
  }

  // Handle cross-section dragging
  if (drag.workoutSectionId !== drop.workoutSectionId) {
    handleCrossSectionExerciseMove(workouts, drag, drop, mutationFns);
    return;
  }

  // Handle same-section dragging to "AddWorkoutExercise"
  if (drop.type === 'AddWorkoutExercise') {
    const dropFindResult = findSection(
      workouts,
      drop.workoutId,
      drop.workoutSectionId,
    );
    if (!dropFindResult) {
      return;
    }

    // If the exercise is part of a superset, move the entire superset
    if (drag.workoutSupersetId) {
      mutationFns.onEditSuperset(drag.workoutId, drag.workoutSupersetId, {
        sortOrder: nextSortOrder(dropFindResult.section.entries),
      });
    } else {
      mutationFns.onEditExercise(drag.workoutId, drag.workoutExerciseId, {
        sortOrder: nextSortOrder(dropFindResult.section.entries),
      });
    }
    return;
  }

  if (
    drag.workoutSupersetId !== null &&
    drag.workoutSupersetId === drop.workoutSupersetId
  ) {
    const dragFindResult = findSupersetExercise(
      workouts,
      drag.workoutId,
      drag.workoutSectionId,
      drag.workoutExerciseId,
      drag.workoutSupersetId,
    );
    const dropFindResult = findSupersetExercise(
      workouts,
      drop.workoutId,
      drop.workoutSectionId,
      drop.workoutExerciseId,
      drop.workoutSupersetId,
    );
    if (!dropFindResult || !dragFindResult) {
      return;
    }

    mutationFns.onEditSupersetExercise(
      drag.workoutId,
      dragFindResult.supersetExercise.workoutSupersetExerciseId,
      {
        sortOrder: sortOrderForDnD(
          dragFindResult.supersetExercise,
          dropFindResult.supersetExercise,
          dropFindResult.superset.exercises,
        ),
      },
    );
    return;
  }

  const dropFindResult = findEntry(
    workouts,
    drop.workoutId,
    drop.workoutSectionId,
    drop.workoutExerciseId,
    drop.workoutSupersetId,
    drop.workoutAlternateGroupId,
  );
  const dragFindResult = findEntry(
    workouts,
    drag.workoutId,
    drag.workoutSectionId,
    drag.workoutExerciseId,
    drag.workoutSupersetId,
    drag.workoutAlternateGroupId,
  );
  if (!dropFindResult || !dragFindResult) {
    return;
  }

  const sortOrder = sortOrderForDnD(
    dragFindResult.entry,
    dropFindResult.entry,
    dropFindResult.section.entries,
  );

  if (dragFindResult.entry.__type === 'WorkoutExercise') {
    mutationFns.onEditExercise(
      drag.workoutId,
      dragFindResult.entry.workoutExerciseId,
      {
        sortOrder,
      },
    );
  } else if (dragFindResult.entry.__type === 'WorkoutSuperset') {
    mutationFns.onEditSuperset(
      drag.workoutId,
      dragFindResult.entry.workoutSupersetId,
      {
        sortOrder,
      },
    );
  }
}

function handleSectionDnD(
  workouts: Workout[],
  drag: WorkoutSectionDraggable,
  drop: WorkoutSectionDroppable,
  mutationFns: WorkoutLayerMutationFns,
) {
  if (
    drop.type === 'WorkoutSection' &&
    drag.workoutSectionId === drop.workoutSectionId
  ) {
    return;
  }
  if (drag.workoutId !== drop.workoutId) {
    return;
  }

  if (drop.type === 'AddWorkoutSection') {
    const workout = findWorkout(workouts, drop.workoutId);
    if (!workout) {
      return;
    }

    mutationFns.onEditSection(drag.workoutId, drag.workoutSectionId, {
      sortOrder: nextSortOrder(workout.sections),
    });
    return;
  }

  const dropFindResult = findSection(
    workouts,
    drop.workoutId,
    drop.workoutSectionId,
  );
  const dragFindResult = findSection(
    workouts,
    drag.workoutId,
    drag.workoutSectionId,
  );
  if (!dropFindResult || !dragFindResult) {
    return;
  }

  mutationFns.onEditSection(drag.workoutId, drag.workoutSectionId, {
    sortOrder: sortOrderForDnD(
      dragFindResult.section,
      dropFindResult.section,
      dropFindResult.workout.sections,
    ),
  });
}

export type DndStyleInfo = {
  showDragIcon: boolean;
  highlight: boolean;
  isDragging: boolean;
};

export function useDndRow<T extends WorkoutSectionDroppable | WorkoutExerciseDroppable>(
  id: string,
  data: T,
  styleInfoFn: (active: Active | null, hovering: boolean, isOver: boolean, dropData: T) => DndStyleInfo,
  disabled = false,
) {
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    setActivatorNodeRef,
    transform,
    active,
  } = useDraggable({ id, data, disabled });
  const { isOver, setNodeRef: setDroppableRef } = useDroppable({ id, data });
  const [hovering, setHovering] = useState(false);
  const { showDragIcon, highlight, isDragging } = styleInfoFn(active, hovering, isOver, data);

  return {
    showDragIcon,
    highlight,
    isDragging,
    transform,
    setDraggableRef,
    setActivatorNodeRef,
    setDroppableRef,
    listeners,
    attributes,
    setHovering,
  };
}

export function sectionDndStyleInfo(
  active: Active | null,
  hovering: boolean,
  isOver: boolean,
  dropData: WorkoutSectionDroppable,
): DndStyleInfo {
  const dragData = active?.data.current as AnyDraggable | undefined;

  const isDragging =
    dragData?.type === 'WorkoutSection' &&
    dragData?.workoutSectionId === dropData.workoutSectionId;

  const isDroppable =
    !isDragging &&
    dragData?.type === 'WorkoutSection' &&
    dragData.workoutId === dropData.workoutId;

  const showDragIcon = (hovering && !dragData) || isDroppable || isDragging;

  const highlight = isOver && isDroppable && !isDragging;

  return {
    showDragIcon,
    highlight,
    isDragging,
  };
}

export function exerciseDndStyleInfo(
  active: Active | null,
  hovering: boolean,
  isOver: boolean,
  dropData: WorkoutExerciseDroppable,
): DndStyleInfo {
  const dragData = active?.data.current as AnyDraggable | undefined;

  const isDragging =
    dragData?.type === 'WorkoutExercise' &&
    dragData?.workoutExerciseId === dropData.workoutExerciseId;

  const isDroppable =
    !isDragging &&
    dragData?.type === 'WorkoutExercise' &&
    dragData.workoutId === dropData.workoutId;

  const showDragIcon = (hovering && !dragData) || isDroppable || isDragging;

  const highlight = isOver && isDroppable && !isDragging;

  return {
    showDragIcon,
    highlight,
    isDragging,
  };
}

function findWorkout(
  workouts: Workout[],
  workoutId: string,
): Workout | undefined {
  return workouts.find((workout) => workout.workoutId === workoutId);
}

function findSection(
  workouts: Workout[],
  workoutId: string,
  workoutSectionId: string,
):
  | {
      workout: Workout;
      section: WorkoutSection;
    }
  | undefined {
  const workout = findWorkout(workouts, workoutId);
  const section = workout?.sections.find(
    (section) => section.workoutSectionId === workoutSectionId,
  );
  if (workout && section) {
    return { workout, section };
  }
  return undefined;
}

function findEntry(
  workout: Workout[],
  workoutId: string,
  workoutSectionId: string,
  workoutExerciseId: string,
  workoutSupersetId: string | null,
  workoutAlternateGroupId?: string | null,
):
  | {
      section: WorkoutSection;
      entry: WorkoutSuperset | WorkoutExercise | WorkoutAlternateGroup;
    }
  | undefined {
  const findResult = findSection(workout, workoutId, workoutSectionId);
  if (!findResult) {
    return;
  }
  const section = findResult.section;

  if (workoutSupersetId) {
    const superset = section.entries.find(
      (e): e is WorkoutSuperset =>
        e.__type === 'WorkoutSuperset' &&
        e.workoutSupersetId === workoutSupersetId,
    );
    return superset && { section, entry: superset };
  }

  if (workoutAlternateGroupId) {
    const alternateGroup = section.entries.find(
      (e): e is WorkoutAlternateGroup =>
        e.__type === 'WorkoutAlternateGroup' &&
        e.workoutAlternateGroupId === workoutAlternateGroupId,
    );
    return alternateGroup && { section, entry: alternateGroup };
  }

  const exercise = section.entries.find(
    (e): e is WorkoutExercise =>
      e.__type === 'WorkoutExercise' &&
      e.workoutExerciseId === workoutExerciseId,
  );
  return exercise && { section, entry: exercise };
}

function findSupersetExercise(
  workout: Workout[],
  workoutId: string,
  workoutSectionId: string,
  workoutExerciseId: string,
  workoutSupersetId: string,
):
  | {
      superset: WorkoutSuperset;
      supersetExercise: WorkoutSupersetExercise;
    }
  | undefined {
  const findResult = findSection(workout, workoutId, workoutSectionId);
  if (!findResult) {
    return;
  }
  const section = findResult.section;

  const superset = section.entries.find(
    (e): e is WorkoutSuperset =>
      e.__type === 'WorkoutSuperset' &&
      e.workoutSupersetId === workoutSupersetId,
  );
  const supersetExercise = superset?.exercises.find(
    (e) => e.workoutExerciseId === workoutExerciseId,
  );
  if (superset && supersetExercise) {
    return { superset, supersetExercise };
  }
  return undefined;
}

function handleExerciseMetricDnD(
  workouts: Workout[],
  drag: WorkoutExerciseMetricDraggable,
  drop: WorkoutExerciseMetricDroppable,
  mutationFns: WorkoutLayerMutationFns,
) {
  // Only allow reordering within the same exercise
  if (drag.workoutExerciseId !== drop.workoutExerciseId) {
    return;
  }

  // Don't do anything if dragging to the same position
  if (drag.workoutExerciseMetricId === drop.workoutExerciseMetricId) {
    return;
  }
  const exercise = findExerciseMetrics(
    workouts,
    drag.workoutId,
    drag.workoutSectionId,
    drag.workoutExerciseId,
  );

  if (!exercise) {
    return;
  }

  const dragMetric = exercise.metrics.find(
    (m) => m.workoutExerciseMetricId === drag.workoutExerciseMetricId,
  );
  const dropMetric = exercise.metrics.find(
    (m) => m.workoutExerciseMetricId === drop.workoutExerciseMetricId,
  );

  if (!dragMetric || !dropMetric) {
    return;
  }

  // Don't allow moving non-sets metrics before sets metric
  const isDropSetsMetric = dropMetric.metricType === 'sets';
  const isDragSetsMetric = dragMetric.metricType === 'sets';

  if (isDropSetsMetric && !isDragSetsMetric) {
    return; // Can't move non-sets metric to position before sets
  }

  if (!isDropSetsMetric && isDragSetsMetric) {
    return; // Can't move sets metric away from first position
  }

  const sortOrder = sortOrderForDnD(dragMetric, dropMetric, exercise.metrics);

  mutationFns.onEditExerciseMetric(
    drag.workoutId,
    drag.workoutExerciseMetricId,
    { sortOrder },
  );
}

function findExerciseMetrics(
  workouts: Workout[],
  workoutId: string,
  workoutSectionId: string,
  workoutExerciseId: string,
): WorkoutExercise | undefined {
  const findResult = findSection(workouts, workoutId, workoutSectionId);
  if (!findResult) {
    return undefined;
  }

  for (const entry of findResult.section.entries) {
    if (
      entry.__type === 'WorkoutExercise' &&
      entry.workoutExerciseId === workoutExerciseId
    ) {
      return entry;
    } else if (entry.__type === 'WorkoutSuperset') {
      const exercise = entry.exercises.find(
        (ex) => ex.exercise.workoutExerciseId === workoutExerciseId,
      )?.exercise;
      if (exercise) {
        return exercise;
      }
    } else if (entry.__type === 'WorkoutAlternateGroup') {
      const exercise = entry.exercises.find(
        (ex) => ex.exercise.workoutExerciseId === workoutExerciseId,
      )?.exercise;
      if (exercise) {
        return exercise;
      }
    }
  }

  return undefined;
}

function sortOrderForDnD(
  drag: Sortable,
  drop: Sortable,
  items: Sortable[],
): number {
  return targetSortOrderByFn(
    items,
    (n) => n.sortOrder,
    drop.sortOrder,
    drag.sortOrder > drop.sortOrder ? 'before' : 'after',
  );
}
